import React, { useState } from "react";
import { Spinner } from "flowbite-react";
import { Calendar, Clock, Users, User, Video, CheckCircle, XCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { NoCreditModal, ConfirmParticipationModal } from "./CreditModals";
import SessionCardSkeleton from "./SessionCardSkeleton";

export interface PublicAppointment {
  id: string;
  therapistName: string;
  therapistId?: string;
  patientName?: string;
  patientId?: string;
  description?: string;
  date: Date;
  duration: number;
  status: "scheduled" | "in-progress" | "completed";
  roomId?: string;
  public: boolean;
  expectedParticipants?: number;
}

interface PublicSessionsListProps {
  appointments: PublicAppointment[];
  isLoading?: boolean;
  userParticipations?: Set<string>;
  schedulingAppointment?: string | null;
  schedulingError?: { [key: string]: string };
  schedulingSuccess?: { [key: string]: boolean };
  onScheduleParticipation?: (appointmentId: string) => void;
  emptyMessage?: string;
  currentUserId?: string;
  participationCredits?: number;
  onViewPlans?: () => void;
}

const getInitials = (name: string) => {
  if (!name) return "TT";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

const PublicSessionsList: React.FC<PublicSessionsListProps> = ({
  appointments,
  isLoading = false,
  userParticipations = new Set(),
  schedulingAppointment = null,
  schedulingError = {},
  schedulingSuccess = {},
  onScheduleParticipation,
  emptyMessage = "Nenhuma sessão futura disponível no momento.",
  currentUserId,
  participationCredits = 0,
  onViewPlans,
}) => {
  const [showNoCreditModal, setShowNoCreditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins > 0 ? ` ${mins}min` : ""}`;
    }
    return `${mins}min`;
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <SessionCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div className="rounded-[1.75rem] border border-dashed border-[#E0EDE3] bg-white/80 px-6 py-12 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#E0EDE3]">
          <Calendar className="h-7 w-7 text-[#2B5A41]" aria-hidden="true" />
        </div>
        <p className="mt-4 text-sm text-[#5F7167]">{emptyMessage}</p>
        <p className="mt-2 text-xs text-[#89A78C]">Novas sessões serão agendadas em breve</p>
      </div>
    );
  }

  const handleScheduleClick = (appointmentId: string) => {
    if (participationCredits > 0) {
      setSelectedAppointmentId(appointmentId);
      setShowConfirmModal(true);
    } else {
      setShowNoCreditModal(true);
    }
  };

  const confirmScheduleParticipation = () => {
    if (selectedAppointmentId) {
      onScheduleParticipation?.(selectedAppointmentId);
      setShowConfirmModal(false);
      setSelectedAppointmentId(null);
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {appointments.map((appointment) => (
          <article
            key={appointment.id}
            className="flex h-full flex-col rounded-[1.75rem] border border-[#E0EDE3] bg-white p-6 shadow-[0_18px_40px_rgba(43,90,65,0.1)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_50px_rgba(43,90,65,0.14)]"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#2B5A41] text-sm font-bold text-white">
                {getInitials(appointment.therapistName)}
              </div>
              <h3 className="truncate font-semibold text-[#2B5A41]">{appointment.therapistName}</h3>
            </div>

            {appointment.description && (
              <p className="mt-3 text-sm leading-6 text-[#5F7167]">{appointment.description}</p>
            )}

            <div className="mt-4 space-y-2">
              <div className="flex items-center text-sm text-[#5F7167]">
                <Clock className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
                {formatDate(appointment.date)}
              </div>
              <div className="flex items-center text-sm text-[#5F7167]">
                <Users className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
                Duração: {formatDuration(appointment.duration)}
              </div>
              {appointment.expectedParticipants !== undefined && (
                <div className="flex items-center text-sm text-[#5F7167]">
                  <User className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
                  {appointment.expectedParticipants} participante{appointment.expectedParticipants !== 1 ? "s" : ""} previsto
                  {appointment.expectedParticipants !== 1 ? "s" : ""}
                </div>
              )}
            </div>

            <div className="mt-5 space-y-3">
              {appointment.roomId && appointment.status === "in-progress" && (
                <Link to={`/sala/${appointment.roomId}`} className="block w-full">
                  <span className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2B5A41] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#203C2E]">
                    <Video className="h-4 w-4" aria-hidden="true" />
                    Entrar na Consulta
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </span>
                </Link>
              )}

              {onScheduleParticipation && (
                <>
                  {userParticipations.has(appointment.id) ||
                  (currentUserId && (appointment.therapistId === currentUserId || appointment.patientId === currentUserId)) ? (
                    <span className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-green-600 px-5 py-2.5 text-sm font-semibold text-white">
                      <CheckCircle className="h-4 w-4" aria-hidden="true" />
                      Participação Agendada
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2B5A41] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#203C2E] disabled:opacity-60"
                      onClick={() => handleScheduleClick(appointment.id)}
                      disabled={schedulingAppointment === appointment.id}
                    >
                      {schedulingAppointment === appointment.id ? (
                        <>
                          <Spinner size="sm" aria-label="Agendando participação" />
                          Agendando...
                        </>
                      ) : (
                        <>
                          <Calendar className="h-4 w-4" aria-hidden="true" />
                          Agendar Participação
                        </>
                      )}
                    </button>
                  )}

                  {schedulingError[appointment.id] && (
                    <div className="flex items-center text-sm text-red-600">
                      <XCircle className="mr-1 h-4 w-4 shrink-0" aria-hidden="true" />
                      <span>{schedulingError[appointment.id]}</span>
                    </div>
                  )}

                  {schedulingSuccess[appointment.id] && (
                    <div className="flex items-center text-sm text-green-600">
                      <CheckCircle className="mr-1 h-4 w-4 shrink-0" aria-hidden="true" />
                      <span>Participação agendada com sucesso!</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </article>
        ))}
      </div>

      <NoCreditModal
        isOpen={showNoCreditModal}
        onClose={() => setShowNoCreditModal(false)}
        onViewPlans={onViewPlans || (() => {})}
        type="participation"
      />

      <ConfirmParticipationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setSelectedAppointmentId(null);
        }}
        onConfirm={confirmScheduleParticipation}
        isProcessing={schedulingAppointment === selectedAppointmentId}
        participationCredits={participationCredits}
      />
    </>
  );
};

export default PublicSessionsList;
